import * as Yup from 'yup';
import Decorator from 'clientDecorator';

// Constants
const RECIPIENT_TYPES = {
  TO: 'TO',
  CC: 'CC',
  BCC: 'BCC',
  FROM: 'FROM',
};

const DEFAULT_DATE_FORMAT = {
  year: 'numeric',
  month: 'long',
  day: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
};

// Reusable schema definitions
const emailArraySchema = Yup.array().of(Yup.string().email('Invalid email address'));
const nullableString = Yup.string().nullable();
const nullableNumber = Yup.number().nullable();
const nullableBoolean = Yup.boolean().nullable();

const attachmentRequestSchema = Yup.object().shape({
  signed_url: Yup.string().required('Signed URL is required'),
  is_inline: Yup.boolean().required('Inline flag is required'),
  content_id: Yup.string(),
  content_disposition: Yup.string().required('Content disposition is required'),
});

// Main email schema
export const emailSchema = Yup.object().shape({
  // Required fields
  to: emailArraySchema.required('To is required'),

  // Optional email arrays
  cc: emailArraySchema,
  bcc: emailArraySchema,
  reply_to: emailArraySchema.nullable(),

  // String fields
  subject: Yup.string(),
  body: Yup.string(),
  status: nullableString,
  nylas_schedule_id: nullableString,
  nylas_thread_id: nullableString,
  nylas_message_id: nullableString,
  nylas_draft_id: nullableString,
  interaction_id: nullableString,
  trip_id: nullableString,
  reply_to_message_id: nullableString,
  id: nullableString,

  // Number fields
  task_id: nullableNumber,
  email_template_id: nullableNumber,
  sent_at: nullableNumber, // Unix timestamp in seconds
  send_at: nullableNumber, // Unix timestamp in seconds
  scheduled_at: nullableNumber, // Unix timestamp in seconds

  // Complex fields
  attachment_requests: Yup.array().of(attachmentRequestSchema).nullable(),
  attachments: Yup.array().nullable(),
  from: Yup.object().nullable(),
  thankview_urls: Yup.array().nullable(),

  // Boolean fields
  unread: nullableBoolean,
  starred: nullableBoolean,
});

// Helper functions
const getPrimaryEmail = (contact) => {
  const { email: profileEmail = {} } = Decorator.Profile.getPrimaryContactInfo(contact);
  return profileEmail?.email || contact.emails?.filter((email) => email.primary)?.[0]?.email || '';
};

const expandRecipients = (recipients = []) => {
  return recipients.reduce(
    (acc, recipient) => {
      const type = recipient.recipient_type;
      const email = recipient.email_address;

      switch (type) {
        case RECIPIENT_TYPES.TO:
          acc.to.push(email);
          break;
        case RECIPIENT_TYPES.CC:
          acc.cc.push(email);
          break;
        case RECIPIENT_TYPES.BCC:
          acc.bcc.push(email);
          break;
        case RECIPIENT_TYPES.FROM:
          acc.from.push(email);
          break;
      }
      return acc;
    },
    { to: [], cc: [], bcc: [], from: [] }
  );
};

const formatReplyBody = (email) => {
  const { body, sent_at } = email;
  const fromRecipient = email.email_recipients.find((r) => r.recipient_type === RECIPIENT_TYPES.FROM);
  const email_address = fromRecipient?.email_address || '';
  const formattedEmail = email_address ? `&lt;${email_address}&gt;` : '';
  const date = new Date(sent_at * 1000).toLocaleString('en-US', DEFAULT_DATE_FORMAT);

  return `
    <div><br></div>
    <div class="et-quote-body">
      <div class="quote header">
        On ${date} ${formattedEmail} wrote:
      </div>
      <div><br></div>
      <blockquote class="et-quote" style="margin: 0px 0px 0px 0.8ex; border-left: 1px solid rgb(204, 204, 204); padding-left: 1ex;">
        ${body}
      </blockquote>
    </div>
    `;
};

const getDefaultRecipients = (email, contact, clickedEmailAddress) => {
  const { to, cc, bcc, from } = expandRecipients(email?.email_recipients);
  const primaryEmail = getPrimaryEmail(contact);

  return {
    to: from.length
      ? from
      : to?.length
        ? to
        : clickedEmailAddress
          ? [clickedEmailAddress]
          : primaryEmail
            ? [primaryEmail]
            : [],
    cc: cc?.length ? cc : [],
    bcc: bcc?.length ? bcc : [],
    from,
  };
};

const getEmailBody = (email, defaultSignature, getFormattedSignature) => {
  if (email?.body && email.nylas_message_id) {
    // The check below ensures that the email body is not formatted after a scheduled email created through Reply is canceled.
    if (email.sent_at) return formatReplyBody(email);
    else return email.body;
  }

  if (email?.body && !email.nylas_message_id) {
    return email.body;
  }

  return (defaultSignature.signature_body && getFormattedSignature(defaultSignature)) || '';
};

// Main exported functions
export const setDefaultEmailValues = (
  email = {},
  contact = {},
  defaultSignature = {},
  getFormattedSignature = () => {},
  clickedEmailAddress = '',
  taskId = null
) => {
  const recipients = getDefaultRecipients(email, contact, clickedEmailAddress);
  const body = getEmailBody(email, defaultSignature, getFormattedSignature);

  return {
    // Recipients
    ...recipients,

    // Content
    subject: email?.subject || '',
    body,

    // IDs and metadata
    task_id: taskId ? taskId : email?.task_id || null,
    email_template_id: email?.email_template_id || null,
    nylas_schedule_id: email?.nylas_schedule_id || null,
    nylas_thread_id: email?.nylas_thread_id || null,
    nylas_message_id: email?.nylas_message_id || null,
    nylas_draft_id: email?.nylas_draft_id || null,
    interaction_id: email?.interaction_id || null,
    trip_id: email?.trip_id || null,
    reply_to_message_id: email?.reply_to_message_id || null,
    id: email?.id || null,

    // Attachments
    attachment_requests: email?.attachment_requests?.length ? email?.attachment_requests : [],
    attachments: email?.attachments?.length ? email?.attachments : [],

    // Timestamps
    sent_at: email?.sent_at || null,
    send_at: email?.send_at || null,
    scheduled_at: email?.scheduled_at || null,

    // Status fields
    status: email?.status || null,
    reply_to: email?.reply_to || null,
    unread: email?.unread || true,
    starred: email?.starred || false,
  };
};

/**
 * Build the `attachment_requests` array the outreach send endpoint
 * (`outreach/v1/email/send`) expects, from the uploader's in-memory file
 * objects. Unlike the cadence `event_config` shape (keyed by `content_id` and
 * resolved to a signed URL by the backend at send time), this endpoint takes
 * the already-signed `signed_url` directly.
 *
 * @param {Array} files - file objects from useFileUploader
 * @returns {Array<{signed_url: string, is_inline: boolean, content_id: string, content_disposition: string}>}
 */
export const buildAttachmentRequests = (files = []) => {
  return files.map((f) => ({
    signed_url: f.url,
    is_inline: f.isInline,
    content_id: f.contentId || '',
    content_disposition: f.contentDisposition || 'attachment',
  }));
};

const buildMessageRecipients = (values) => ({
  subject: values?.subject || '',
  to: values?.to.map((email) => ({ email })),
  cc: values?.cc.map((email) => ({ email })),
  bcc: values?.bcc.map((email) => ({ email })),
  body: values?.body || '',
  nylas_thread_id: values?.nylas_thread_id || null,
  reply_to_message_id: values?.nylas_thread_id || null,
});

const getEmailTemplateId = (values) => {
  return values?.email_template_id && values?.body.includes(`data-template-id="${values?.email_template_id}"`)
    ? values?.email_template_id
    : null;
};

const extractThankViewUrls = (body) => {
  const urls = new Set();

  // Find all occurrences of "Click" and "to see your video!"
  const contextRegex = /Click[\s\S]*?to see your video!/g;
  const linkRegex = /<a[^>]*href="([^"]+)"[^>]*>/g;

  let contextMatch;
  while ((contextMatch = contextRegex.exec(body)) !== null) {
    const context = contextMatch[0];

    // Extract all links within the matched context
    let linkMatch;
    while ((linkMatch = linkRegex.exec(context)) !== null) {
      const url = linkMatch[1].trim();
      urls.add(url);
    }
  }

  return Array.from(urls);
};

const buildTypeSpecificFields = (type, values, message) => {
  const typeFields = { ...message };

  let thankViewUrls = [];
  if ((values?.body || '').includes('to see your video!')) {
    thankViewUrls = extractThankViewUrls(values?.body || '');
  }

  typeFields.thankview_urls = thankViewUrls;

  if (type === 'scheduled') {
    typeFields.send_at = values?.send_at || null;
  }

  if (type === 'update') {
    typeFields.nylas_draft_id = values?.nylas_draft_id || null;
  }

  if (type === 'update' || type === 'scheduled') {
    typeFields.nylas_schedule_id = values?.nylas_schedule_id || null;
    typeFields.unread = values?.unread || true;
    typeFields.starred = values?.starred || false;
  }

  if (values?.nylas_message_id) {
    typeFields.nylas_message_id = values?.nylas_message_id;
  }

  return typeFields;
};

export const buildEmailPayload = (values, type = 'send', files = []) => {
  const baseMessage = {
    ...buildMessageRecipients(values),
    attachment_requests: buildAttachmentRequests(files),
  };

  const message = buildTypeSpecificFields(type, values, baseMessage);

  const payload = {
    message,
    interaction_id: values?.interaction_id || null,
    trip_id: values?.trip_id || null,
    task_id: values?.task_id || null,
    email_template_id: getEmailTemplateId(values),
  };

  return { payload, id: values?.id || null };
};
